import type { Ref } from 'vue';
import type { AccommodationOptions } from './grid-accommodation';
import type {
	AccommodationStrategy,
	BlockWithId,
	GridBlockPosition,
	GridLayoutData,
	GridRow,
	OverlapValidationResult,
	ValidationResult,
} from './grid-types';
import { tryAccommodate as tryAccommodateStrategy } from './grid-accommodation';
import { rangesOverlap, validateNoOverlaps } from './grid-overlap';
import { getBlocksInRow } from './grid-row-management';

/**
 * Grid validation composable
 * Validates block positions and provides smart accommodation strategies
 */
export function useGridValidation(
	gridData: Ref<GridLayoutData>,
) {
	/**
	 * Validate a block position against grid rules
	 */
	function validatePosition(
		rowIndex: number,
		colStart: number,
		colSpan: number,
		excludeChildId: string | null = null,
	): ValidationResult {
		if (colStart < 0) {
			return { valid: false, error: 'colStart_negative' };
		}

		if (colSpan < 1) {
			return { valid: false, error: 'colSpan_too_small' };
		}

		if (rowIndex < 0 || rowIndex >= gridData.value.rows.length) {
			return { valid: false, error: 'exceeds_row_bounds' };
		}

		const row = gridData.value.rows[rowIndex]!;

		if (colStart + colSpan > row.columns) {
			return { valid: false, error: 'exceeds_row_bounds' };
		}

		for (const [childId, pos] of Object.entries(gridData.value.blockPositions)) {
			if (childId === excludeChildId) continue;
			if (pos.rowIndex !== rowIndex) continue;

			const blockEnd = colStart + colSpan;
			const otherEnd = pos.colStart + pos.colSpan;

			if (rangesOverlap(colStart, blockEnd, pos.colStart, otherEnd)) {
				return { valid: false, error: 'overlap', conflictingChildId: childId };
			}
		}

		return { valid: true };
	}

	/**
	 * Get all blocks in a specific row, sorted by colStart
	 */
	function getRowBlocks(rowIndex: number, excludeChildId?: string): BlockWithId[] {
		return getBlocksInRow(rowIndex, gridData.value.blockPositions, excludeChildId);
	}

	/**
	 * Try to accommodate a block position using various strategies
	 */
	function tryAccommodate(
		childId: string,
		rowIndex: number,
		colStart: number,
		colSpan: number,
		options?: AccommodationOptions,
	): AccommodationStrategy {
		return tryAccommodateStrategy(
			{
				gridData: gridData.value,
				validatePosition,
				getRowBlocks,
			},
			childId,
			rowIndex,
			colStart,
			colSpan,
			options,
		);
	}

	/**
	 * Validate a complete positions map before applying changes
	 */
	function validateAllPositions(
		positions: Record<string, GridBlockPosition>,
		rows?: Array<GridRow>,
	): OverlapValidationResult {
		return validateNoOverlaps(positions, rows ?? gridData.value.rows);
	}

	return {
		validatePosition,
		tryAccommodate,
		getRowBlocks,
		validateAllPositions,
	};
}
